<template>
  <div class="ak-popout-preview h-full relative">
    <ak-loading
      v-model:active="isLoading"
      :is-full-page="false"
      :can-cancel="false"
      :spinner-size="'default'"
    />
    <ak-empty-state
      v-show="!isLoading && !computedModel.formId"
      title="No form"
      icon="alert-circled"
    >
      Please select a form to continue
    </ak-empty-state>
    <iframe
      v-if="displayFrame"
      id="preivewFrame"
      ref="frame"
      class="h-full"
    ></iframe>
  </div>
</template>

<script>
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import { sleep } from '@akerolabs/akero-vue/src/utils/helpers'
import { getHTMLContents } from './helpers'
import { defineComponent, computed, ref, onMounted, watch } from 'vue'

export default defineComponent({
  name: 'AkPopoutEditorPreview',

  components: {
    [Loading.name]: Loading,
    [EmptyState.name]: EmptyState,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const displayFrame = ref(false)
    const isLoading = ref(false)
    const frame = ref(null)

    const computedModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const renderIframeContents = async () => {
      // First we are going to put the loading screen up to hide
      // the stuff going on in the background
      isLoading.value = true

      // new we need to destroy the iframe to prevent JS errors
      //we are going to make use of the vue `v-if` to do this
      displayFrame.value = false
      await sleep(10)

      // Now we need to add the iframe back in, this will give us a
      // fresh dom to work with and prevent any JS errors in the
      // frame
      displayFrame.value = true
      await sleep(10)

      // Now we can start to write the contents of the iframe document
      // to inject the xConvert script and intitialize it, we dont
      // need to set the `isLoading.value` after this as we are going
      // to listen for postMessage events to do this.
      const html = getHTMLContents(computedModel.value)

      try {
        const { document: doc } = frame.value.contentWindow
        doc.open()
        doc.write(html)
        doc.close()
      } catch (ex) {
        console.log(ex)
      }
    }

    watch(
      computedModel,
      async () => {
        await renderIframeContents()
      },
      { deep: true },
    )

    onMounted(async () => {
      if (computedModel.value.formId) {
        await renderIframeContents()
      }
    })

    window.addEventListener(
      'message',
      ({ data }) => {
        if (data === 'previewRenderComplete') {
          isLoading.value = false
        }
      },
      false,
    )

    return {
      computedModel,
      isLoading,
      displayFrame,
      frame,
    }
  },
})
</script>

<style lang="scss">
.ak-popout-preview {
  .ak-loading-overlay__backdrop--bg {
    background-color: white;
  }
}
</style>
